import { useField } from "formik";
import type { ComponentType, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { t } from "ttag";

import type { ActionFormFieldProps } from "metabase/actions/types";
import { useDispatch } from "metabase/lib/redux";
import { fetchDashboardParameterValues } from "metabase/parameters/actions";
import type {
  ActionFormOption,
  DashboardId,
  FieldValue,
  Parameter,
} from "metabase-types/api";

interface DashboardParameterOptionsWidgetProps {
  Widget: ComponentType<any>;
  formField: ActionFormFieldProps;
  dashboardId: DashboardId;
  parameter: Parameter;
  parameters: Parameter[];
  hidden?: boolean;
  actions?: ReactNode;
  innerRef?: any;
}

/**
 * Renders a dropdown / inline select for an action parameter that is mapped to
 * a dashboard filter, using that filter's own value list as the options.
 *
 * Values are fetched with the same thunk the dashboard filter widget uses, so
 * this shares the parameter value cache with the widget, picks up linked-filter
 * narrowing, and gets remapped display labels for free.
 *
 * Note that the endpoint resolves values from the filter's connections to card
 * columns. If the filter is only wired to the action button, it returns an
 * empty list. In that case (or if the current value simply isn't part of the
 * fetched list) we still keep the field's current value selectable, so a
 * default coming from initialValues (e.g. an `actionParam_{slug}` URL param)
 * is preserved instead of appearing unset.
 *
 * The underlying `Select` matches the selected option by strict equality, so
 * a fetched option whose value is the same filter value but a different type
 * (e.g. a numeric id from the filter's value list vs. a string default from
 * an `actionParam_{slug}` URL param) won't register as selected even though
 * it's "the same" value. We normalize the matched option's value to the
 * field's actual value so the selection persists once the real list loads.
 */
export function DashboardParameterOptionsWidget({
  Widget,
  formField,
  dashboardId,
  parameter,
  parameters,
  hidden,
  actions,
  innerRef,
}: DashboardParameterOptionsWidgetProps) {
  const dispatch = useDispatch();
  const [{ value: currentValue }] = useField(formField.name);
  const [values, setValues] = useState<FieldValue[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    setIsLoading(true);
    setHasError(false);

    Promise.resolve(
      dispatch(
        fetchDashboardParameterValues({
          dashboardId,
          entityIdentifier: null,
          parameter,
          parameters,
        }),
      ),
    )
      .then((response) => {
        if (!isCancelled) {
          setValues(response?.values ?? []);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setHasError(true);
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [dispatch, dashboardId, parameter, parameters]);

  const options = useMemo<ActionFormOption[]>(() => {
    const fetchedOptions = (values ?? [])
      .filter(([value]) => value != null)
      .map(([value, label]) => ({
        name: String(label ?? value),
        value: value as string | number,
      }));

    // Keep the field's current value selectable even if it wasn't part of the
    // fetched list (e.g. a default coming from an actionParam_{slug} URL
    // param, or a dashboard filter that isn't wired to any card column).
    const hasCurrentValue = currentValue != null && currentValue !== "";
    const currentValueIndex = fetchedOptions.findIndex(
      (option) => String(option.value) === String(currentValue),
    );

    if (!hasCurrentValue) {
      return fetchedOptions;
    }

    if (currentValueIndex === -1) {
      return [
        { name: String(currentValue), value: currentValue as string | number },
        ...fetchedOptions,
      ];
    }

    const matchedOption = fetchedOptions[currentValueIndex];
    if (matchedOption.value === currentValue) {
      return fetchedOptions;
    }

    // Same value, different type (e.g. fetched number vs. string URL param) -
    // realign it to the field's actual value so it keeps matching by strict
    // equality once this becomes the sole source of truth for the option.
    const normalizedOptions = [...fetchedOptions];
    normalizedOptions[currentValueIndex] = {
      ...matchedOption,
      value: currentValue as string | number,
    };
    return normalizedOptions;
  }, [values, currentValue]);

  const { options: _staticOptions, ...rest } = formField;

  const placeholder = isLoading
    ? t`Loading…`
    : hasError
      ? t`Couldn't load options`
      : options.length === 0
        ? t`No values available for this filter`
        : formField.placeholder;

  return (
    <Widget
      {...rest}
      options={options}
      placeholder={placeholder}
      disabled={hidden || isLoading || hasError || options.length === 0}
      actions={actions}
      nullable
      ref={innerRef}
    />
  );
}
